import { Theme, css } from '@emotion/react';

import { useUserQuery } from '@hooks/query/useUserQuery';
import { Radius, Spacing, Typography } from 'chatie-design-system';

import SwipeableListContainer from '@components/common/SwipableListContainer';

import { IChatCharacter, IChatSituationDetail } from '@model';
import { HEADER_HEIGHT } from '@styles/css';

import CharacterProfileCard from './CharacterProfileCard';

const containerCSS = (theme: Theme) => css`
  margin-top: ${HEADER_HEIGHT}px;

  .maker-name {
    ${Typography.body12};
    color: ${theme.var.gray600};
    text-align: center;
    margin: 20px 0;
  }
  .chemi-container {
    display: flex;
    flex-direction: column;
    gap: 5px;
    margin-bottom: 20px;
    margin-top: 12px;
  }

  .padding16 {
    padding: 0 16px;
  }

  .subtitle {
    padding: 10px 16px;
    ${Typography.h5};
  }

  .desc {
    color: ${theme.var.gray500};
    ${Typography.body12_sentence};
    margin-top: -4px;
    margin-bottom: 4px;
    padding: 0 16px;
  }

  .scroll-container {
    display: flex;
    flex: 1;
    overflow-x: auto;
    gap: 12px;

    &::-webkit-scrollbar {
      display: none;
    }
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .chip-container {
    display: flex;
    flex: 1;
    gap: 10px;
    padding: 10px 16px;
  }
  .fill {
    width: 100%;
  }
  .right8 {
    margin-right: 8px;
  }
  .highlight {
    color: ${theme.colors.main700};
  }
`;

const characterCardContainerCSS = css`
  display: flex;
  align-items: flex-start;
  padding-top: ${Spacing.large}px;

  .heart {
    ${Typography.h5};
    margin-top: 58px;
  }
`;

const chipCSS = (theme: Theme) => css`
  display: flex;
  white-space: nowrap;
  padding: 10px 12px;
  background-color: ${theme.colors.main10};
  border-radius: ${Radius.medium}px;
  color: ${theme.var.gray900};
  ${Typography.body14_sentence};
`;

interface ICharacterChemiInfoAreaProps {
  character: IChatCharacter;
  situationInfo: IChatSituationDetail;
  // handleNavigateUser: (id: number) => void;
  handleClickTag: (id: number, name: string) => void;
  handleClickStep: () => void;
}

const CharacterChemiInfoArea = ({
  character,
  situationInfo,
  // handleNavigateUser,
  handleClickTag,
  handleClickStep,
}: ICharacterChemiInfoAreaProps) => {
  const { useFetchUser } = useUserQuery();
  const { data: user } = useFetchUser(character?.player?.user_id);

  const stepTitle = situationInfo.chemi_story.step_set.map(
    (v) => `${'step' + v.number + '. ' + v.title}`
  );

  const infoList = [
    {
      id: 'step',
      name: '우리의 관계 step',
      value: stepTitle,
      desc: '스텝을 클릭하면 줄거리가 나와요.',
    },
  ];

  const makerInfo = situationInfo?.maker_note
    ? {
        id: 'maker_note',
        name: `💬 ${user?.nickname}님의 메이커 노트`,
        value: [situationInfo?.maker_note],
      }
    : {
        id: 'maker_note',
        name: `💬 메이커 ${user?.nickname}`,
        value: [],
      };

  const hasMakerNote = !!situationInfo?.maker_note;

  return (
    <div css={containerCSS}>
      <div css={characterCardContainerCSS}>
        <CharacterProfileCard
          character={character}
          handleClickTag={handleClickTag}
          type={'character'}
        />
        <p className="heart">💘</p>
        <CharacterProfileCard
          character={character.related_profile as IChatCharacter}
          handleClickTag={handleClickTag}
          type={'profile'}
        />
      </div>
      <div className="chemi-container">
        {infoList.map((item) => (
          <div className="chemi-info">
            <h5 className="subtitle">{item.name}</h5>
            {item.desc && <p className="desc">{item.desc}</p>}
            <SwipeableListContainer className="scroll-container">
              <div className="chip-container">
                {item.value.map((v) => (
                  <div
                    css={chipCSS}
                    className={`chip ${item.id === 'maker_note' ? 'fill' : ''}`}
                    onClick={() => item.id === 'step' && handleClickStep()}
                  >
                    {v}
                  </div>
                ))}
              </div>
            </SwipeableListContainer>
          </div>
        ))}
        <div className="chemi-info">
          <h5 className="subtitle">
            {!hasMakerNote && <span className="right8">{'💬 메이커'}</span>}
            <span className="highlight">
              {hasMakerNote ? `💬 ${user?.nickname}` : `${user?.nickname}`}
            </span>
            {hasMakerNote && <span>{'님의 메이커 노트'}</span>}
          </h5>
          {hasMakerNote && (
            <div className="chip-container">
              <div css={chipCSS} className={`chip fill`}>
                <p className="multiline">{makerInfo.value}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CharacterChemiInfoArea;
